import { LabReportTemplate } from './types';
import { CURRENT_SCHEMA_VERSION, DocumentSection } from '../../core/ast/document.types';
import { createDefaultPageSettings, createDefaultMetadata } from '../../core/ast/factory';
import { labReportCoverTemplate } from '../cover/labReportCover';
import { getDefaultCoverValues } from '../cover/templateUtils';

export const diodeCharacteristicsLabReportTemplate: LabReportTemplate = {
  id: 'tpl-lab-diode-characteristics',
  name: 'Diode I-V Characteristics & Modeling (CSE-251 Expt 01)',
  category: 'electronics',
  description: 'Complete lab report blueprint with cover page, mathematical formulas (Shockley diode model, ideality factor, piecewise linear resistance), experimental datasheet, and post-lab analysis.',
  thumbnailBadge: 'Diode I-V',
  version: 2,
  sectionsBlueprint: [
    { title: 'Cover Page', type: 'cover', defaultContent: '' },
    { title: 'Title', type: 'title', defaultContent: 'Title: I-V Characteristics and Modeling of Forward Conduction of a Diode.' },
    { title: 'Objectives', type: 'objectives', defaultContent: '1. To measure the I-V characteristics of forward conduction of a p-n junction diode.\n2. To determine the models of forward conduction of a p-n junction diode.' },
    { title: 'Introduction', type: 'theory', defaultContent: 'Diode is one of the most basic nonlinear electronic devices. An ideal diode acts like a switch for electric current, acting as a short circuit for current flow in one direction (forward bias connection) while behaving as an open circuit for current flow in the opposite direction (reverse bias connection). The characteristics of practical diodes are somewhat different from those of ideal ones. The p-n junction diodes are one of the most popular diodes used in the industry. The forward bias current-voltage (I-V) characteristic of a p-n junction diode is measured and modeled in this experiment.' },
    { title: 'Circuit Diagram', type: 'custom', defaultContent: 'Circuit configuration for measuring forward-bias I-V diode characteristics.' },
    { title: 'Experimental Datasheet', type: 'data', defaultContent: 'Table 1. Experimental Datasheet for Forward Bias Measurements.' },
    { title: 'Post-Lab Report Questions and Solutions', type: 'custom', defaultContent: 'Mathematical calculations for diode ideality factor (n), reverse saturation current (Is), cut-in voltage, and piecewise linear resistance (rD).' },
    { title: 'Discussion', type: 'discussion', defaultContent: 'This experiment successfully examined the I-V characteristics of a p-n junction diode in forward conduction. The observed results closely matched the expected exponential behavior.' },
  ],
  compileToDocument: (customTitle?: string, pageSettingsOverrides?) => {
    const now = new Date().toISOString();

    // 1. Cover Page Section
    const coverVals = getDefaultCoverValues({
      documentTypeLabel: 'Lab Report',
      title: 'Lab Report',
      experimentNumber: '01',
      experimentName: 'I-V Characteristics and Modeling of Forward Conduction of a Diode.',
      groupNumber: '',
      courseCode: 'CSE251',
      courseTitle: 'Electronic Circuits',
      sectionOrGroup: '',
      semester: '',
      studentName: '',
      studentId: '',
      facultyName: '',
      facultyDesignation: '',
      facultyDepartment: '',
      dateOfPerformance: '',
      submissionDate: '',
      submissionDisplayMode: 'both',
    });
    const coverDoc = labReportCoverTemplate.compileToDocument(coverVals, pageSettingsOverrides);
    const coverSection = coverDoc.sections[0];

    // 2. Report Content Sections
    const sections: DocumentSection[] = [
      coverSection,
      // Section 1: Title & Objectives
      {
        id: `sec-diode-title-${Date.now().toString(36)}`,
        title: 'Title & Objectives',
        type: 'title',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-d-t1-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Title: I-V Characteristics and Modeling of Forward Conduction of a Diode.',
              formatting: { bold: true, fontSizePt: 13, color: '#111827' },
            },
          },
          {
            id: `blk-d-sp1-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: { text: '\n', formatting: { fontSizePt: 8 } },
          },
          {
            id: `blk-d-obj-h-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Objectives:',
              formatting: { bold: true, fontSizePt: 12, color: '#111827' },
            },
          },
          {
            id: `blk-d-obj-body-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: '1. To measure the I-V characteristics of forward conduction of a p-n junction diode.\n2. To determine the models of forward conduction of a p-n junction diode.',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.4 },
            },
          },
        ],
      },
      // Section 2: Introduction & Theory
      {
        id: `sec-diode-theory-${Date.now().toString(36)}`,
        title: 'Introduction & Theory',
        type: 'theory',
        enabled: true,
        collapsed: false,
        blocks: [
          {
            id: `blk-d-th-h-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Introduction:',
              formatting: { bold: true, fontSizePt: 12, color: '#111827' },
            },
          },
          {
            id: `blk-d-th-body-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Diode is one of the most basic nonlinear electronic devices. An ideal diode acts like a switch for electric current, acting as a short circuit for current flow in one direction (forward bias connection) while behaving as an open circuit for current flow in the opposite direction (reverse bias connection). The characteristics of practical diodes are somewhat different from those of ideal ones. The p-n junction diodes are one of the most popular diodes used in the industry. The forward bias current-voltage (I-V) characteristic of a p-n junction diode will be measured in this experiment.',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.4 },
            },
          },
          {
            id: `blk-d-th-f1-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: 'I_D = I_S \\left( e^{\\frac{V_D}{n V_T}} - 1 \\right)',
              displayMode: true,
              equationNumber: '(Eq. 1)',
              caption: 'Shockley Diode Forward Conduction Model (VT = 0.0259 V)',
            },
          },
        ],
      },
      // Section 3: Circuit Diagram
      {
        id: `sec-diode-circuit-${Date.now().toString(36)}`,
        title: 'Circuit Diagram',
        type: 'custom',
        enabled: true,
        collapsed: false,
        blocks: [
          {
            id: `blk-d-circ-h-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Circuit Diagram:\nFigure-1: Circuit set up to measure forward bias I-V characteristics of a diode',
              formatting: { bold: true, fontSizePt: 11, color: '#1f2937' },
            },
          },
        ],
      },
      // Section 4: Experimental Datasheet
      {
        id: `sec-diode-data-${Date.now().toString(36)}`,
        title: 'Experimental Datasheet',
        type: 'data',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-d-dt-h-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Table 1. Experimental Datasheet:',
              formatting: { bold: true, fontSizePt: 12, color: '#111827' },
            },
          },
          {
            id: `blk-d-dt-tbl-${Date.now().toString(36)}`,
            type: 'table',
            createdAt: now,
            data: {
              rows: 15,
              cols: 5,
              hasHeaderRow: true,
              hasHeaderCol: false,
              borderStyle: 'academic',
              caption: 'Table 1: Diode Forward Bias Measurement Datasheet',
              cells: [
                [
                  { id: 'c00', content: 'Vs (V)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c01', content: 'VD (V)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c02', content: 'VR (V)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c03', content: 'ID (mA) = VR / R', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c04', content: 'Measured R (kΩ)', isHeader: true, bold: true, alignment: 'center' },
                ],
                [
                  { id: 'c10', content: '0.5', alignment: 'center' },
                  { id: 'c11', content: '0.34', alignment: 'center' },
                  { id: 'c12', content: '0.016', alignment: 'center' },
                  { id: 'c13', content: '0.01481', alignment: 'center' },
                  { id: 'c14', content: '1.08 K', alignment: 'center' },
                ],
                [
                  { id: 'c20', content: '1.0', alignment: 'center' },
                  { id: 'c21', content: '0.18', alignment: 'center' },
                  { id: 'c22', content: '0.048', alignment: 'center' },
                  { id: 'c23', content: '0.04444', alignment: 'center' },
                  { id: 'c24', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c30', content: '2.0', alignment: 'center' },
                  { id: 'c31', content: '0.51', alignment: 'center' },
                  { id: 'c32', content: '1.16', alignment: 'center' },
                  { id: 'c33', content: '1.07407', alignment: 'center' },
                  { id: 'c34', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c40', content: '3.0', alignment: 'center' },
                  { id: 'c41', content: '0.29', alignment: 'center' },
                  { id: 'c42', content: '1.94', alignment: 'center' },
                  { id: 'c43', content: '1.79630', alignment: 'center' },
                  { id: 'c44', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c50', content: '4.0', alignment: 'center' },
                  { id: 'c51', content: '0.31', alignment: 'center' },
                  { id: 'c52', content: '3.70', alignment: 'center' },
                  { id: 'c53', content: '3.42593', alignment: 'center' },
                  { id: 'c54', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c60', content: '5.0', alignment: 'center' },
                  { id: 'c61', content: '0.32', alignment: 'center' },
                  { id: 'c62', content: '4.80', alignment: 'center' },
                  { id: 'c63', content: '4.44444', alignment: 'center' },
                  { id: 'c64', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c70', content: '6.0', alignment: 'center' },
                  { id: 'c71', content: '0.33', alignment: 'center' },
                  { id: 'c72', content: '5.80', alignment: 'center' },
                  { id: 'c73', content: '5.37037', alignment: 'center' },
                  { id: 'c74', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c80', content: '7.0', alignment: 'center' },
                  { id: 'c81', content: '0.34', alignment: 'center' },
                  { id: 'c82', content: '6.90', alignment: 'center' },
                  { id: 'c83', content: '6.38889', alignment: 'center' },
                  { id: 'c84', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c90', content: '8.0', alignment: 'center' },
                  { id: 'c91', content: '0.35', alignment: 'center' },
                  { id: 'c92', content: '7.94', alignment: 'center' },
                  { id: 'c93', content: '7.35185', alignment: 'center' },
                  { id: 'c94', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c100', content: '10.0', alignment: 'center' },
                  { id: 'c101', content: '0.36', alignment: 'center' },
                  { id: 'c102', content: '10.00', alignment: 'center' },
                  { id: 'c103', content: '9.25926', alignment: 'center' },
                  { id: 'c104', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c110', content: '12.0', alignment: 'center' },
                  { id: 'c111', content: '0.38', alignment: 'center' },
                  { id: 'c112', content: '12.20', alignment: 'center' },
                  { id: 'c113', content: '11.29630', alignment: 'center' },
                  { id: 'c114', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c120', content: '14.0', alignment: 'center' },
                  { id: 'c121', content: '0.39', alignment: 'center' },
                  { id: 'c122', content: '14.32', alignment: 'center' },
                  { id: 'c123', content: '13.25926', alignment: 'center' },
                  { id: 'c124', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c130', content: '16.0', alignment: 'center' },
                  { id: 'c131', content: '0.410', alignment: 'center' },
                  { id: 'c132', content: '16.40', alignment: 'center' },
                  { id: 'c133', content: '15.18519', alignment: 'center' },
                  { id: 'c134', content: '', alignment: 'center' },
                ],
                [
                  { id: 'c140', content: '17.5', alignment: 'center' },
                  { id: 'c141', content: '0.419', alignment: 'center' },
                  { id: 'c142', content: '18.00', alignment: 'center' },
                  { id: 'c143', content: '16.66667', alignment: 'center' },
                  { id: 'c144', content: '', alignment: 'center' },
                ],
              ],
            },
          },
        ],
      },
      // Section 5: Post-Lab Report Questions & Solutions
      {
        id: `sec-diode-postlab-${Date.now().toString(36)}`,
        title: 'Post-Lab Questions & Solutions',
        type: 'custom',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-d-pl-h-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Post-Lab Report Questions and Solutions:\n\n1. Using MATLAB, plot the I-V characteristics of the p-n junction diode in forward conduction. Label the axes appropriately.\n\n2. Use the data points corresponding to ID1 = 2 mA (VD1 = 0.52 V) and ID2 = 2.5 mA (VD2 = 0.53 V) to calculate diode parameters Is and n using VT = 0.0259 V.',
              formatting: { bold: true, fontSizePt: 10.5, color: '#111827', lineHeightMultiplier: 1.4 },
            },
          },
          {
            id: `blk-d-pl-sol1-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: 'n = \\frac{V_{D2} - V_{D1}}{V_T \\ln\\left(\\frac{I_{D2}}{I_{D1}}\\right)} = \\frac{0.53 - 0.52}{0.0259 \\times \\ln(2.5 / 2.0)} = 1.73',
              displayMode: true,
              equationNumber: '(Eq. 2)',
              caption: 'Ideality Factor (n) Calculation',
            },
          },
          {
            id: `blk-d-pl-sol2-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: 'I_S = \\frac{I_D}{e^{\\frac{V_D}{n V_T}}} = \\frac{2\\text{ mA}}{e^{\\frac{0.52}{1.73 \\times 0.0259}}} = 0.0182\\text{ mA}',
              displayMode: true,
              equationNumber: '(Eq. 3)',
              caption: 'Reverse Saturation Current (Is) Calculation',
            },
          },
          {
            id: `blk-d-pl-q3-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: '3. Determine the cut-in voltage from the plotted graph:\nFrom the experimental tangent line extrapolation, the cut-in voltage is found to be approximately 0.682 V.\n\n4. Calculate the piecewise linear model dynamic resistance (RD):\nRD = (VD2 - VD1) / (ID2 - ID1) = (0.626 V - 0.615 V) / (2.5 mA - 2.0 mA) = 0.022 kΩ (22 Ω).',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.4 },
            },
          },
        ],
      },
      // Section 6: Discussion
      {
        id: `sec-diode-disc-${Date.now().toString(36)}`,
        title: 'Discussion',
        type: 'discussion',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-d-disc-body-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Discussion:\n\nThis experiment successfully examined the I-V characteristics of a p-n junction diode in forward conduction. The observed experimental results closely matched the expected exponential behavior.\n\nThe experiment data and I-V characteristics curve graph clearly illustrate the typical forward bias behavior of a diode. Initially at low forward voltages (VD), the current (ID) through the diode was very small, indicating that the diode was in its non-conducting state. As the forward voltage increased beyond the barrier threshold, the diode began to conduct significantly, and current increased rapidly after the cut-in voltage (approximately 0.682 V for the measured silicon diode).\n\nFrom the Shockley diode equation, the calculated ideality factor n was 1.73 (which falls within the expected physical range of 1 to 2 for silicon p-n junctions) and reverse saturation current IS was 0.0182 mA.\n\nOverall, the experiment successfully characterized the forward conduction parameters of the diode. Minor discrepancies in dynamic resistance can be attributed to breadboard contact resistance, meter internal impedance, and slight thermal heating during sustained current trials.',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.5 },
            },
          },
        ],
      },
    ];

    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      metadata: createDefaultMetadata(customTitle || 'CSE-251 Lab Report 01: Diode Characteristics', 'lab_report'),
      pageSettings: { ...createDefaultPageSettings(), ...pageSettingsOverrides },
      sections,
    };
  },
};
